import { useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { ErrorBanner } from '@/components/ErrorBanner';
import { useLanguage } from '@/i18n/LanguageContext';
import type { LocationActions } from '@/api/types';
import { describeSetupError } from './setup';
import { downloadLocationLabels } from './labels';

export type BrowsableCell = LocationActions & { id: string; code: string; barcode?: string; kind: string; is_pickable: boolean; pick_sequence?: number; has_stock: boolean };

/**
 * Cells of one zone (or the "other places" list) as touch-sized buttons; the selected one opens ONE panel below the
 * grid: rename, walking order / pickability, archive and delete. The server decides what is allowed (`can_delete`,
 * `delete_blocker`, `can_archive`, `archive_blocker`) - this component only renders that verdict with a human reason,
 * and a destructive action always goes through an explicit confirm step.
 */
export function CellsBrowser({
  warehouseId,
  cells,
  canEdit,
  onChanged,
  onNotice,
}: {
  warehouseId: string;
  cells: readonly BrowsableCell[];
  canEdit: boolean;
  onChanged: () => void;
  onNotice: (message: string) => void;
}): JSX.Element {
  const { t } = useLanguage();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = cells.find((c) => c.id === selectedId) ?? null;
  return (
    <>
      <ul className="cell-grid">
        {cells.map((c) => (
          <li key={c.id} className={c.has_stock ? 'has-stock' : ''}>
            {canEdit ? (
              <button
                type="button"
                className={`cell-btn${selectedId === c.id ? ' selected' : ''}`}
                aria-expanded={selectedId === c.id}
                aria-label={t('cells.manage', { code: c.code })}
                title={c.has_stock ? t('zones.cellHasStock') : t('zones.cellEmpty')}
                onClick={() => setSelectedId(selectedId === c.id ? null : c.id)}
              >
                <span className="mono">{c.code}</span>
              </button>
            ) : (
              <span title={c.has_stock ? t('zones.cellHasStock') : t('zones.cellEmpty')}>
                <span className="mono">{c.code}</span>
                <span className="sr-only">{c.has_stock ? t('zones.cellHasStock') : t('zones.cellEmpty')}</span>
              </span>
            )}
          </li>
        ))}
      </ul>
      {canEdit && selected && (
        <CellPanel
          key={selected.id}
          warehouseId={warehouseId}
          cell={selected}
          onClose={() => setSelectedId(null)}
          onChanged={onChanged}
          onNotice={(m) => {
            setSelectedId(null);
            onNotice(m);
          }}
        />
      )}
    </>
  );
}

type Mode = 'edit' | 'archive' | 'delete';

function CellPanel({
  warehouseId,
  cell,
  onClose,
  onChanged,
  onNotice,
}: {
  warehouseId: string;
  cell: BrowsableCell;
  onClose: () => void;
  onChanged: () => void;
  onNotice: (message: string) => void;
}): JSX.Element {
  const { t } = useLanguage();
  const [mode, setMode] = useState<Mode>('edit');
  const [code, setCode] = useState(cell.code);
  const [pickable, setPickable] = useState(cell.is_pickable);
  const [seq, setSeq] = useState(String(cell.pick_sequence ?? 0));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const idp = `cell-${cell.id}`;

  const seqNum = seq.trim() === '' ? NaN : Number(seq);
  const seqValid = Number.isInteger(seqNum) && seqNum >= 0 && seqNum <= 1_000_000;
  const normalised = code.trim().toUpperCase();
  const codeValid = /^[A-Z0-9][A-Z0-9-]{0,39}$/.test(normalised);
  const dirty = normalised !== cell.code || pickable !== cell.is_pickable || (seqValid && seqNum !== (cell.pick_sequence ?? 0));

  const attempt = (fn: () => Promise<void>): void => {
    setBusy(true);
    setError(null);
    setSaved(null);
    fn()
      .catch(setError)
      .finally(() => setBusy(false));
  };
  const errText = error ? describeSetupError(t, error) : null;

  const save = (): void =>
    attempt(async () => {
      const r = await endpoints.updateLocation(warehouseId, cell.id, {
        ...(normalised !== cell.code ? { code: normalised } : {}),
        ...(pickable !== cell.is_pickable ? { isPickable: pickable } : {}),
        ...(seqValid && seqNum !== (cell.pick_sequence ?? 0) ? { pickSequence: seqNum } : {}),
      });
      setSaved(r.labelsOutdated ? t('cells.labelsOutdated', { code: r.code }) : t('cells.saved'));
      onChanged();
    });

  return (
    <section className="sub-form cell-panel" aria-label={t('cells.panelTitle', { code: cell.code })}>
      <h3>{t('cells.panelTitle', { code: cell.code })}</h3>

      {mode === 'edit' && (
        <>
          {cell.barcode && (
            <div className="barcode-row">
              <span className="field-hint">{t('labels.cellBarcode')} <span className="mono">{cell.barcode}</span></span>
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  attempt(async () => {
                    const r = await downloadLocationLabels(warehouseId, { scope: 'SELECTED', locationIds: [cell.id], layout: 'LABEL_100x60' });
                    setSaved(t('labels.done', { n: r.labelCount, pages: r.pageCount }));
                  })
                }
              >
                {t('labels.printOne')}
              </button>
            </div>
          )}
          <div className="field-row">
            <label htmlFor={`${idp}-code`}>{t('cells.codeLabel')}</label>
            <input
              id={`${idp}-code`}
              value={code}
              maxLength={40}
              autoCapitalize="characters"
              autoComplete="off"
              disabled={busy}
              aria-describedby={`${idp}-code-hint`}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
            />
            <p id={`${idp}-code-hint`} className="field-hint">{t('cells.renameWarning')}</p>
            {!codeValid && <p className="preview-line warn" role="alert">{t('cells.err.codeInvalid')}</p>}
          </div>
          {cell.kind === 'BIN' && (
            <label className="check-row">
              <input type="checkbox" checked={pickable} disabled={busy} onChange={(e) => setPickable(e.target.checked)} />
              <span>{t('cells.pickable')}</span>
            </label>
          )}
          <div className="field-row">
            <label htmlFor={`${idp}-seq`}>{t('cells.sequence')}</label>
            <input id={`${idp}-seq`} type="number" inputMode="numeric" min={0} max={1000000} value={seq} disabled={busy} onChange={(e) => setSeq(e.target.value)} />
          </div>
          {saved && <p className="ok-note" role="status">{saved}</p>}
          {error !== null && (errText ? <div className="error-banner" role="alert">{errText}</div> : <ErrorBanner error={error} />)}
          <div className="form-actions">
            <button type="button" className="primary" disabled={busy || !dirty || !codeValid || !seqValid} onClick={save}>
              {busy ? t('warehouses.creating') : t('common.save')}
            </button>
            <button type="button" onClick={onClose} disabled={busy}>{t('zones.close')}</button>
          </div>

          <div className="danger-zone">
            {cell.can_archive === false && cell.archive_blocker && (
              <p className="field-hint">{t(`cells.archiveBlocked.${cell.archive_blocker}` as never)}</p>
            )}
            {cell.can_delete === false && cell.delete_blocker && cell.delete_blocker !== 'SYSTEM' && (
              <p className="field-hint">{t(`cells.deleteBlocked.${cell.delete_blocker}` as never)}</p>
            )}
            {cell.can_delete && <p className="field-hint">{t('cells.deleteHint')}</p>}
            <div className="form-actions">
              <button type="button" disabled={busy || cell.can_archive === false} onClick={() => { setError(null); setMode('archive'); }}>
                {t('cells.archive')}
              </button>
              {cell.can_delete && (
                <button type="button" className="danger" disabled={busy} onClick={() => { setError(null); setMode('delete'); }}>
                  {t('cells.delete')}
                </button>
              )}
            </div>
          </div>
        </>
      )}

      {mode === 'archive' && (
        <>
          <p role="alert">{t('cells.archiveExplain', { code: cell.code })}</p>
          {error !== null && (errText ? <div className="error-banner" role="alert">{errText}</div> : <ErrorBanner error={error} />)}
          <div className="form-actions">
            <button
              type="button"
              className="primary"
              disabled={busy}
              onClick={() =>
                attempt(async () => {
                  await endpoints.archiveLocation(warehouseId, cell.id);
                  onChanged();
                  onNotice(t('cells.archivedDone', { code: cell.code }));
                })
              }
            >
              {t('cells.confirmArchive')}
            </button>
            <button type="button" onClick={() => setMode('edit')} disabled={busy}>{t('common.cancel')}</button>
          </div>
        </>
      )}

      {mode === 'delete' && (
        <>
          <p role="alert">{t('cells.deleteConfirm', { code: cell.code })}</p>
          {error !== null && (errText ? <div className="error-banner" role="alert">{errText}</div> : <ErrorBanner error={error} />)}
          <div className="form-actions">
            <button
              type="button"
              className="danger"
              disabled={busy}
              onClick={() =>
                attempt(async () => {
                  await endpoints.deleteLocation(warehouseId, cell.id);
                  onChanged();
                  onNotice(t('cells.deletedDone', { code: cell.code }));
                })
              }
            >
              {t('cells.confirmDelete')}
            </button>
            <button type="button" onClick={() => setMode('edit')} disabled={busy}>{t('common.cancel')}</button>
          </div>
        </>
      )}
    </section>
  );
}
